import React, { useState, useEffect } from 'react'
import { Modal, Button, InputGroup, FormControl, Spinner, Alert } from 'react-bootstrap'

interface ResetPasswordModalProps {
    show: boolean
    onHide: () => void
    onSubmit: (newPassword: string) => Promise<void>
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ show, onHide, onSubmit }) => {
    const [password, setPassword] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [passwordError, setPasswordError] = useState<string | null>(null)

    const generateRandomPassword = () => {
        const randomString = Math.random().toString(36).slice(-8)
        setPassword(randomString)
    }

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value)
    }

    const validateForm = () => {
        if (!password.trim()) {
            setPasswordError('Password is required')
            return false
        }
        if (password.length < 8) {
            setPasswordError('Password must be at least 8 characters long')
            return false
        }
        setPasswordError(null)
        return true
    }

    const handleSubmit = async () => {
        if (!validateForm()) return

        setLoading(true)
        setError(null)
        try {
            await onSubmit(password)
            setLoading(false)
            handleClose() 
        } catch (err) {
            setLoading(false)
            setError('Failed to reset password. Please try again.')
        }
    }

    const handleClose = () => {
        setPassword('')
        setError(null)
        setPasswordError(null)
        onHide()
    }

    useEffect(() => {
        if (!show) {
            setPassword('')
            setError(null)
            setPasswordError(null)
        }
    }, [show])

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Reset Password</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <InputGroup>
                    <FormControl
                        type="text"
                        placeholder="Enter new password"
                        value={password}
                        onChange={handlePasswordChange}
                        isInvalid={!!passwordError}
                        disabled={loading}
                    />
                    <Button variant="secondary" onClick={generateRandomPassword} disabled={loading}>
                        Generate
                    </Button>
                    {passwordError && (
                        <FormControl.Feedback type="invalid" className="d-block">
                            {passwordError}
                        </FormControl.Feedback>
                    )}
                </InputGroup>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Close
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />{' '}
                            Resetting...
                        </>
                    ) : (
                        'Reset Password'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default ResetPasswordModal
